import * as React from 'react';
import { styles, SvgContainer, Container, ContentContainer, InfoContainer } from './styles';
import { ApolloError } from 'apollo-client';
import { Columns } from '@withjoy/joykit';
import { JoyLogo } from '@routes/event/create/DesignDemo/components/JoyLogo';
import InlineSVG from 'svg-inline-react';
import { BaseButton } from '@routes/event/create/components/BaseButton';
import MediaQuery from 'react-responsive';
import { ErrorMessage } from './components/ErrorMessage';
import { makeDebugger } from '@lib';
import { intercomService } from '@services/tracking/intercom.service';
import { useAnalytics, Analytics } from '@withjoy/web-core';
import { PagePrefix } from '@withjoy/web-core/lib/analytics/context';

const debug = makeDebugger('Error');

interface Props {
  error: ApolloError | ApolloError[];
  attemptedAction: string;
  category: string;
  pagePrefix: PagePrefix;
  page: string;
}

export interface ErrorInfo {
  stack: string;
  message: string;
}

interface ErrorProps {
  analytics: Analytics;
  error: ApolloError | ApolloError[];
  attemptedAction: string;
}

class ErrorComponent extends React.PureComponent<ErrorProps> {
  onClick = () => {
    const { error, analytics } = this.props;
    let errorInfo = { stack: '', message: '' };
    if (Array.isArray(error)) {
      errorInfo = error.reduce((accumulator: ErrorInfo, currentValue: ApolloError) => {
        accumulator.stack += `${currentValue.stack}\n`;
        accumulator.message += `${currentValue.message}\n`;
        return accumulator;
      }, errorInfo);
    } else {
      errorInfo.message = error.message ? error.message : 'Unknown Error';
      errorInfo.stack = error.stack ? error.stack : 'No Error Stack';
    }

    const intercomMessage = `Hi Joy,\nI'm seeing an error when loading the site.\nCan you help me out with this?\n\n(Please don't remove this information. This will help us figure out the issue faster!)\n\n${
      errorInfo.message
    }\n\n${errorInfo.stack}`;

    if (intercomService.active) {
      intercomService.showNewMessage(intercomMessage);
    } else {
      window.location.href = `mailto:hello@withjoy.com?subject=Help&body=${encodeURIComponent(intercomMessage)}`;
    }

    analytics.trackError({
      attemptedAction: this.props.attemptedAction,
      errorDescription: errorInfo.message,
      error: 'Bliss'
    });
  };
  render(): React.ReactNode {
    const { error } = this.props;
    debug(error);
    return (
      <MediaQuery maxWidth={992}>
        {isTablet => {
          return (
            <Container>
              <Columns>
                <Columns.Column mobile="full" tablet="full" desktopOffset={2} desktop={4}>
                  <div className={styles.logoContainer(isTablet)}>
                    <a href="https://www.withjoy.com">
                      <JoyLogo />
                    </a>
                  </div>
                </Columns.Column>
              </Columns>

              <ContentContainer>
                <Columns multiline={true}>
                  <Columns.Column
                    mobile="full"
                    tablet="full"
                    desktopOffset={2}
                    desktop={4}
                    // widescreenOffset={2}
                    // widescreen={4}
                  >
                    <SvgContainer>
                      <InlineSVG className={styles.errorSvg(isTablet)} src={require('./assets/error.svg')} />
                    </SvgContainer>
                  </Columns.Column>
                  <Columns.Column mobile="full" tablet="full" desktop={4} widescreen={4}>
                    <InfoContainer>
                      <div className={styles.messageContainer(isTablet)}>
                        <p>Oops! Looks like something went wrong.</p>
                        {/* <p>No worries :) Try refreshing.</p> */}
                        <BaseButton
                          className={styles.refreshButton}
                          onClick={() => {
                            location.reload();
                          }}
                        >
                          <span>Try Again</span>
                        </BaseButton>
                      </div>
                      <div className={styles.contactContainer}>
                        <ErrorMessage error={error} />
                        <br />
                        <BaseButton className={styles.contactButton} onClick={this.onClick}>
                          <span>Contact Us</span>
                        </BaseButton>
                      </div>
                    </InfoContainer>
                  </Columns.Column>
                </Columns>
              </ContentContainer>
            </Container>
          );
        }}
      </MediaQuery>
    );
  }
}

export const ErrorPage: React.SFC<Props> = ({ page, pagePrefix, category, ...props }) => {
  const analytics = useAnalytics({
    pagePrefix,
    category,
    page
  });

  return <ErrorComponent analytics={analytics} {...props} />;
};
